import { ReactElement, useCallback, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import { ProcurationFeeItemsResponse, procurationFeesValidateDelete, procurationFeesCreateDeleteCr } from '@pmp/api';
import { CrudMode } from '@ohpen/enso-ui';

import { ProcurationFeesTabProps } from '../interfaces';
import { getInitialState, reducer } from '../reducer';
import { ProcurationFeesList } from './ProcurationFeesList';
import { ProcurationFeesCrudForm } from './ProcurationFeesCrudForm';
import { ConfirmCrDeleteModal } from '@pmp/components';

export const ProcurationFeesTab = ({ valueLists, data, isFetching, fetchData }: ProcurationFeesTabProps): ReactElement => {
  const [state, dispatch] = useReducer(reducer<ProcurationFeeItemsResponse>, getInitialState<ProcurationFeeItemsResponse>());
  const { t } = useTranslation();

  const onCreate = useCallback(() => {
    dispatch({ type: 'openCrud', record: null, mode: CrudMode.CREATE });
  }, []);

  const onView = useCallback((record: ProcurationFeeItemsResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.VIEW });
  }, []);

  const onEdit = useCallback((record: ProcurationFeeItemsResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.EDIT });
  }, []);

  const onCloseCrud = useCallback(() => {
    dispatch({ type: 'closeCrud' });
  }, []);

  const onDelete = useCallback((record: ProcurationFeeItemsResponse) => {
    dispatch({ type: 'openDeleteModal', record });
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    dispatch({ type: 'closeDeleteModal' });
  }, []);

  return (
    <>
      <ProcurationFeesList
        data={data ?? []}
        isFetching={isFetching}
        valueLists={valueLists}
        onCreate={onCreate}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
      />
      {state.crud.open && state.crud.mode && (
        <ProcurationFeesCrudForm
          mode={state.crud.mode}
          record={state.crud.record}
          valueLists={valueLists}
          refreshTable={fetchData}
          onClose={onCloseCrud}
        />
      )}
      <ConfirmCrDeleteModal
        open={state.deleteModal.open}
        records={state.deleteModal.record ? [state.deleteModal.record] : []}
        idKey="id"
        nameKey="name"
        entityLabel={t('lender.procurationFees.labels.procurationFee')}
        entityLabelPlural={t('lender.procurationFees.labels.procurationFees')}
        getPrecheck={procurationFeesValidateDelete}
        createDeleteChangeRequest={procurationFeesCreateDeleteCr}
        refreshTable={fetchData}
        onClose={onCloseDeleteModal}
      />
    </>
  );
};
